/**
 * ============================================================================
 * QuickCreateModalsContext — Modais de criação rápida "no lugar"
 * ----------------------------------------------------------------------------
 * Objetivo: ao acionar uma ação do botão "+" do header (ou seu atalho de
 * teclado), abrir o modal de criação SOBRE a tela atual, sem navegar para a
 * página do módulo. Isso evita que o usuário perca o contexto (análise,
 * filtros, rolagem) apenas por querer criar um registro.
 *
 * Escopo: apenas as ações marcadas com `inline: true` em
 * `src/config/quickCreate.ts`. "Iniciar conversa" continua navegando para o
 * chat, pois o fluxo exige a tela de conversas.
 *
 * Reaproveitamento: os modais usados aqui são EXATAMENTE os mesmos das
 * páginas (CreateContactDialog, CustomerFormDialog, DealFormDialog,
 * TaskFormDialog e ManualEventModal) — nenhum formulário é duplicado.
 *
 * RELEASE:
 *   2026-08-24 v1.2.0 - Negócio: removido o toast de sucesso; o próprio
 *     DealFormDialog passou a exibir o modal com número do negócio.
 *   2026-08-07 v1.1.0 - Nova chave `qc_contact_note` (anotação com busca
 *     de contato, reaproveitando o modal de anotações do chat).
 *   2026-08-06 v1.0.0 - Criação. Contato, Cliente, Negócio, Tarefa e Evento.
 * ============================================================================
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuth } from '@/hooks/useAuth';
import { useCalendars } from '@/hooks/useCalendars';
import { createLogger } from '@/lib/logger';

import { CreateContactDialog } from '@/components/tenant/contacts/CreateContactDialog';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { ManualEventModal } from '@/components/calendar/ManualEventModal';
import { QuickCreateContactNoteModal } from '@/components/quick-create/QuickCreateContactNoteModal';
// Contexto de permissões: necessário para modais que dependem de RBAC (Tarefa).
import { PermissionsProvider } from '@/contexts/PermissionsContext';

const logger = createLogger('QuickCreateModals');

/** Chaves de ação (mesmas de QUICK_CREATE_ACTIONS) suportadas em modal inline. */
export type QuickCreateModalKey =
  | 'qc_contact'
  | 'qc_customer'
  | 'qc_deal'
  | 'qc_task'
  | 'qc_event_manual'
  | 'qc_contact_note';

/** Conjunto usado para validar se uma chave qualquer é suportada aqui. */
const SUPPORTED_KEYS: readonly QuickCreateModalKey[] = [
  'qc_contact',
  'qc_customer',
  'qc_deal',
  'qc_task',
  'qc_event_manual',
  'qc_contact_note',
];

export function isQuickCreateModalKey(key: string): key is QuickCreateModalKey {
  return (SUPPORTED_KEYS as readonly string[]).includes(key);
}

interface QuickCreateModalsContextValue {
  /** Abre o modal correspondente à ação, sem navegar. */
  openQuickCreate: (key: QuickCreateModalKey) => void;
  /** Fecha qualquer modal aberto. */
  closeQuickCreate: () => void;
  /** Chave atualmente aberta (ou null). */
  activeKey: QuickCreateModalKey | null;
}

const QuickCreateModalsContext = createContext<QuickCreateModalsContextValue | null>(null);

/**
 * Hook de acesso. Retorna `null` quando o provider não está montado
 * (ex.: rotas do painel White Label), permitindo fallback para navegação.
 */
export function useQuickCreateModals(): QuickCreateModalsContextValue | null {
  return useContext(QuickCreateModalsContext);
}

/**
 * Wrapper do modal de evento manual: isola o hook `useCalendars` para que a
 * consulta de calendários só ocorra quando o modal realmente é aberto.
 */
function QuickCreateEventModal({ onClose }: { onClose: () => void }) {
  const { calendars } = useCalendars();
  const queryClient = useQueryClient();

  return (
    <ManualEventModal
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          // Atualiza qualquer visão de calendário aberta na tela atual.
          queryClient.invalidateQueries({ queryKey: ['calendar-unified-events'] });
          queryClient.invalidateQueries({ queryKey: ['calendar-month-summary'] });
        }
      }}
      calendars={calendars}
      event={null}
    />
  );
}

/**
 * Provider global. Montado uma única vez em App.tsx para ficar disponível em
 * todos os layouts do tenant.
 */
export function QuickCreateModalsProvider({ children }: { children: ReactNode }) {
  const [activeKey, setActiveKey] = useState<QuickCreateModalKey | null>(null);
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const openQuickCreate = useCallback((key: QuickCreateModalKey) => {
    logger.debug('Abrindo modal de criação rápida no lugar', { key });
    setActiveKey(key);
  }, []);

  const closeQuickCreate = useCallback(() => setActiveKey(null), []);

  const value = useMemo(
    () => ({ openQuickCreate, closeQuickCreate, activeKey }),
    [openQuickCreate, closeQuickCreate, activeKey],
  );

  /** Fecha o modal quando o consumidor sinaliza `onOpenChange(false)`. */
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeQuickCreate();
    },
    [closeQuickCreate],
  );

  return (
    <QuickCreateModalsContext.Provider value={value}>
      {children}

      {/* --------------------------------------------------------------- */}
      {/* Modais montados sob demanda (só existem enquanto abertos)        */}
      {/*                                                                  */}
      {/* v1.2.0 — Os modais são renderizados dentro de um                 */}
      {/* <PermissionsProvider> aninhado porque este provider vive ACIMA   */}
      {/* das rotas (fora do TenantProtectedRoute, que normalmente monta   */}
      {/* o contexto de permissões). Sem isso, o TaskFormDialog quebra ao  */}
      {/* chamar useTaskPhasePermissions(). Montado apenas quando há um    */}
      {/* modal ativo, evitando fetch de permissões em telas públicas.     */}
      {/* --------------------------------------------------------------- */}
      {activeKey && (
      <PermissionsProvider>
      <>

      {activeKey === 'qc_contact' && currentTenant?.id && (

        <CreateContactDialog
          open
          onOpenChange={handleOpenChange}
          tenantId={currentTenant.id}
          onSuccess={() => {
            // Mantém listagens de contatos abertas em sincronia.
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['contacts-paginated'] });
            // KPIs da tela de Contatos (totais) acompanham a lista.
            queryClient.invalidateQueries({ queryKey: ['contacts-stats'] });
            closeQuickCreate();
          }}

        />
      )}

      {activeKey === 'qc_customer' && (
        <CustomerFormDialog
          customer={null}
          open
          onOpenChange={handleOpenChange}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
            queryClient.invalidateQueries({ queryKey: ['crm-customers-paginated'] });
            closeQuickCreate();
          }}
        />
      )}

      {activeKey === 'qc_deal' && (
        <DealFormDialog
          open
          onOpenChange={handleOpenChange}
          deal={null}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
            queryClient.invalidateQueries({ queryKey: ['crm-deals-kanban'] });
            /* v1.2.0: sem toast e sem fechar aqui — o DealFormDialog exibe o
               modal de sucesso e só então chama onOpenChange(false). */
          }}
        />
      )}

      {activeKey === 'qc_task' && (
        <TaskFormDialog
          open
          onOpenChange={handleOpenChange}
          /* v1.2.0 — atalho genérico: usuário escolhe o tipo (Normal/Retenção/Jurídico)
             entre os que ele tem permissão para criar. */
          allowTypeSelection

          onSuccess={(_taskId, taskNumber) => {
            queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
            queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
            queryClient.invalidateQueries({ queryKey: ['tasks-kanban'] });
            if (taskNumber) toast.success(`Tarefa #${taskNumber} criada.`);
            closeQuickCreate();
          }}
        />
      )}

      {activeKey === 'qc_event_manual' && <QuickCreateEventModal onClose={closeQuickCreate} />}

      {/* v1.1.0 — Anotação: passo 1 busca o contato, passo 2 abre o editor do chat. */}
      {activeKey === 'qc_contact_note' && (
        <QuickCreateContactNoteModal onClose={closeQuickCreate} />
      )}

      </>
      </PermissionsProvider>
      )}
    </QuickCreateModalsContext.Provider>
  );
}
